import {
  useEffect,
  useMemo,
  useRef,
  type FunctionComponent,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge/new';
import { useFeatureGate } from '@atlassian/feature-gates-react';

import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import {
  useInfo,
  useSelectedWorkspace,
} from '../../../Flow/ConsentContextProvider';
import { OAuthConsentFeatureGate } from '../../../FeatureGates/FeatureGates';
import {
  deriveSiteNameFromUrl,
  getAvailableConsentSites,
  parseSelectedSiteUrls,
  serializeSelectedSiteUrls,
  type ConsentSite,
} from '../domain/availableSites';
import { grantedNoPermissions } from '../domain/policyState';
import {
  getOrgGroupHeading,
  getOrgOptionState,
  groupSitesByOrg,
  resolveSiteOrgId,
  type ItemOrgGroup,
} from '../domain/orgMatch';
import { useMcpContext } from '../McpContextProvider';
import { mcpSiteSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import { orgGroupMessages } from '../orgGroupMessages';
import {
  MCP_PERMISSIONS_MANAGEMENT_DOC_URL,
  MCP_ROVO_DOC_URL,
} from '../mcpUrls';

const MCP_SITE_SELECT_INPUT_ID = 'oauth-consent-mcp-site-select';

/** How many sites one consent can select while multi-select is on. */
const MAX_SELECTABLE_SITES = 4;

/**
 * A documentation link to drop into a message's `{learnMoreLink}` slot. Composed
 * rather than handed to an ADS inline message so the link stays clickable — see
 * the `message` prop on MultiSelect.
 */
const docLink = (href: string, label: MessageDescriptor): ReactElement => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    <FormattedMessage {...label} />
  </Link>
);

/**
 * A selectable site with its display name resolved once per site, and the org
 * heading it is listed under while grouping is on (null otherwise).
 */
type NamedConsentSite = ConsentSite & {
  name: string;
  orgGroup?: ItemOrgGroup;
};

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (site: NamedConsentSite): string => site.url;
const getLabel = (site: NamedConsentSite): string => site.name;
// The URL is only worth a second line when it differs from the derived name.
const getDescription = (site: NamedConsentSite): string | null =>
  site.name === site.url ? null : site.url;
// The chip is a single line, so it carries both parts: "Pug | pug.jira-dev.com".
const getChipLabel = (site: NamedConsentSite): string =>
  site.name === site.url ? site.url : `${site.name} | ${site.url}`;

/**
 * Atlassian site picker for the MCP consent screen.
 *
 * Single-select by default: one site is granted per consent, submitted as the
 * `selectedWorkspace` param on Accept (see McpFlowParameters) and policy-checked
 * against `/consent/workspace`. With site/Bitbucket multi-select on it holds up
 * to MAX_SELECTABLE_SITES same-org sites, comma-joined
 * in `selectedWorkspace` (see parseSelectedSiteUrls). Shares the `MultiSelect`
 * component with the Bitbucket workspace selector, so the two fields read as
 * one — avatar tile, name over URL, and the same removable chip for the
 * selection.
 *
 * Behind MCP_GROUP_SITES_BY_ORG the options are listed under non-selectable
 * organization headings, with sites of no named org under "No organization" (see
 * groupByOrg); a response without named orgs keeps the flat list.
 *
 * A successful check that grants nothing is reported in this field's own
 * message slot, reading only `policies.site` so a Bitbucket result never
 * surfaces here. A failed request is shown full-panel by McpPolicyAlerts.
 */
interface SiteSelectorProps {
  /**
   * Where the sole-site prefill is remembered. The review step unmounts this
   * field, so a caller that outlives the step passes its own ref; otherwise
   * Back would prefill again a site the user had cleared.
   */
  prefilledSiteUrlRef?: MutableRefObject<string | null>;
}

const SiteSelector: FunctionComponent<SiteSelectorProps> = ({
  prefilledSiteUrlRef: sharedPrefilledSiteUrlRef,
}): ReactElement => {
  const { formatMessage } = useIntl();
  const info = useInfo();
  const { selectedWorkspace, setSelectedWorkspace } = useSelectedWorkspace();
  const {
    policies,
    hasOrgMismatch,
    siteConstraintOrgId,
    siteOrgIndex,
    isSiteBitbucketMultiSelectEnabled: isMulti,
    isMultiSelectionAllowed,
    isSandboxSiteUrl,
    siteSandboxConstraint,
  } = useMcpContext();
  const maxSelected = isMultiSelectionAllowed ? MAX_SELECTABLE_SITES : 1;
  const shouldSortWorkspaces = useFeatureGate(
    OAuthConsentFeatureGate.MCP_SORT_WORKSPACES,
  );
  const isSiteBypassEnabled = useFeatureGate(
    OAuthConsentFeatureGate.SITE_SELECTOR_BYPASS,
  );
  const shouldGroupByOrg = useFeatureGate(
    OAuthConsentFeatureGate.MCP_GROUP_SITES_BY_ORG,
  );

  const sites = useMemo(() => {
    const namedSites = getAvailableConsentSites(info).map((site) => ({
      ...site,
      // The backend resolves a name only where the URL cannot be read as one
      // (a loom workspace URL is a cloud id), so deriving is the fallback, not
      // the rule — showing "Stage" for stage.loom.com/workspace/cloud/<id>
      // names the wrong thing.
      name: site.name ?? deriveSiteNameFromUrl(site.url),
    }));
    if (shouldSortWorkspaces) {
      namedSites.sort((a, b) => a.url.localeCompare(b.url));
    }
    // Sites stay sorted within each org; the orgs themselves follow the same
    // sort gate, by name.
    return shouldGroupByOrg
      ? groupSitesByOrg(namedSites, info, siteOrgIndex, {
          sortGroups: shouldSortWorkspaces,
        })
      : namedSites;
  }, [
    info.availableSites,
    info.availableWorkspaces,
    info.availableOrganizations,
    shouldSortWorkspaces,
    shouldGroupByOrg,
    siteOrgIndex,
  ]);

  const selectedSiteUrls = useMemo(
    () => parseSelectedSiteUrls(selectedWorkspace),
    [selectedWorkspace],
  );
  const hasSites = sites.length > 0;
  const availableSiteUrls = useMemo(
    () => new Set(sites.map((site) => site.url)),
    [sites],
  );
  const ownPrefilledSiteUrlRef = useRef<string | null>(null);
  const prefilledSiteUrlRef =
    sharedPrefilledSiteUrlRef ?? ownPrefilledSiteUrlRef;

  const onlySiteUrl = sites.length === 1 ? sites[0].url : null;
  useEffect(() => {
    if (selectedWorkspace) {
      // A selection the response no longer offers cannot be granted, so it is
      // dropped rather than left to fail its policy check.
      //
      // SiteBypassBanner is the exception, and the reason this reads the bypass
      // gate. It renders directly above this field (see MCP.tsx) and writes a
      // hand-typed host straight into `selectedWorkspace` — a host that is
      // absent from the offered list by definition, since the banner exists for
      // the case where /consent/info returned no sites at all. Sweeping it away
      // leaves an internal user with a "Use this site" button that visibly does
      // nothing, so while the escape hatch is switched on the offered list is
      // not treated as authoritative. That gate is internal-only, so every
      // production consent still gets the sweep — and the banner is in any case
      // the only writer here that can produce an off-list selection, as the
      // field itself only ever stores a site it offered.
      const offeredUrls = selectedSiteUrls.filter((url) =>
        availableSiteUrls.has(url),
      );
      if (
        offeredUrls.length < selectedSiteUrls.length &&
        !isSiteBypassEnabled
      ) {
        setSelectedWorkspace?.(serializeSelectedSiteUrls(offeredUrls));
      } else if (selectedWorkspace === onlySiteUrl) {
        prefilledSiteUrlRef.current = onlySiteUrl;
      }
      return;
    }

    // Prefill a sole option once, but respect the user clearing its chip.
    if (onlySiteUrl && prefilledSiteUrlRef.current !== onlySiteUrl) {
      prefilledSiteUrlRef.current = onlySiteUrl;
      setSelectedWorkspace?.(onlySiteUrl);
    }
  }, [
    availableSiteUrls,
    isSiteBypassEnabled,
    onlySiteUrl,
    selectedSiteUrls,
    selectedWorkspace,
    setSelectedWorkspace,
  ]);

  // The message branches below take priority over the helpers, so they gate
  // them — and only those branches are errors.
  const noPermissions = grantedNoPermissions(policies.site);
  const canShowHelp = hasSites && !hasOrgMismatch && !noPermissions;

  // Other-org sites stay listed but cannot be picked.
  const { isItemDisabled: isOrgDisabled, helpKind } = getOrgOptionState({
    items: sites,
    constraintOrgId: siteConstraintOrgId,
    getOrgId: (site: NamedConsentSite) =>
      resolveSiteOrgId(siteOrgIndex, site.url),
    hasOwnSelection: selectedSiteUrls.length > 0,
    isMultiSelect: isMulti,
    canShowHelp,
  });

  // Sandbox and production sites are never granted together, so the other kind
  // is listed but disabled as well.
  // A site already selected stays enabled so it can still be unticked.
  const isSandboxDisabled = (site: NamedConsentSite): boolean =>
    siteSandboxConstraint != null &&
    !selectedSiteUrls.includes(site.url) &&
    isSandboxSiteUrl(site.url) !== siteSandboxConstraint;
  const hasSandboxDisabled =
    siteSandboxConstraint != null && sites.some(isSandboxDisabled);
  const isItemDisabled = (site: NamedConsentSite): boolean =>
    Boolean(isOrgDisabled?.(site)) || isSandboxDisabled(site);
  // Org first, matching the order of the helper messages below. The cap comes
  // last: removing a site would not free a site the other rules rule out.
  const isAtSiteCap =
    isMulti &&
    isMultiSelectionAllowed &&
    selectedSiteUrls.length >= maxSelected;
  const getDisabledReason = (site: NamedConsentSite): string | null => {
    if (isOrgDisabled?.(site)) {
      return formatMessage(mcpSiteSelectorMessages.otherOrgSiteTooltip);
    }
    if (isSandboxDisabled(site)) {
      return formatMessage(
        isSandboxSiteUrl(site.url)
          ? mcpSiteSelectorMessages.sandboxSiteTooltip
          : mcpSiteSelectorMessages.productionSiteTooltip,
      );
    }
    if (isAtSiteCap) {
      return formatMessage(mcpSiteSelectorMessages.maxSitesTooltip, {
        maxSelected,
      });
    }
    return null;
  };

  // First rule that applies wins. The doc links are composed into the values
  // rather than handed to an ADS inline message — see `docLink`.
  const messageRules: {
    applies: boolean;
    descriptor: MessageDescriptor;
    // Lazy, so only the winning rule builds its links.
    values?: () => Record<string, ReactNode>;
  }[] = [
    {
      applies: !hasSites,
      descriptor: mcpSiteSelectorMessages.noSites,
      values: () => ({
        learnMoreLink: docLink(
          MCP_ROVO_DOC_URL,
          mcpSiteSelectorMessages.noWorkspacesLearnMore,
        ),
      }),
    },
    {
      // Ahead of the policy result: the two selections cannot be granted
      // together whatever the check returned, and changing one of them is the
      // only way forward — so that is what the field should say.
      applies: hasOrgMismatch,
      descriptor: orgMismatchMessages.orgMismatch,
    },
    {
      // The check succeeded but granted nothing, which in practice means an
      // admin policy blocked it — so point at the guide for managing those
      // policies rather than suggesting another site.
      applies: noPermissions,
      descriptor: mcpSiteSelectorMessages.noPermissions,
      values: () => ({
        learnMoreLink: docLink(
          MCP_PERMISSIONS_MANAGEMENT_DOC_URL,
          mcpSiteSelectorMessages.noPermissionsLearnMore,
        ),
      }),
    },
    {
      // Only once this picker holds a pick of its own: the cap can also come
      // from the other picker's selection.
      applies:
        isMulti && !isMultiSelectionAllowed && selectedSiteUrls.length > 0,
      descriptor: mcpSiteSelectorMessages.singleSiteOrg,
    },
    {
      applies: helpKind === 'empty',
      descriptor: mcpSiteSelectorMessages.noOrgSites,
    },
    {
      applies: helpKind === 'constraint',
      descriptor: mcpSiteSelectorMessages.orgConstraint,
    },
    {
      applies: hasSandboxDisabled,
      descriptor: mcpSiteSelectorMessages.sandboxConstraint,
    },
    {
      // Nothing more specific to say, so state the cap and the org rule.
      applies: isMulti,
      descriptor: mcpSiteSelectorMessages.multiSelectHint,
      values: () => ({ maxSelected }),
    },
  ];
  const rule = messageRules.find(({ applies }) => applies);
  const getBadge = (site: NamedConsentSite): ReactNode =>
    isSandboxSiteUrl(site.url) ? (
      <Lozenge appearance="neutral">
        {formatMessage(mcpSiteSelectorMessages.sandboxLozenge)}
      </Lozenge>
    ) : null;

  const message: ReactNode = rule && (
    <FormattedMessage {...rule.descriptor} values={rule.values?.()} />
  );
  const noOrgGroupLabel = formatMessage(orgGroupMessages.noOrganization);

  return (
    <MultiSelect<NamedConsentSite, string>
      inputId={MCP_SITE_SELECT_INPUT_ID}
      items={sites}
      value={selectedSiteUrls}
      toValue={toValue}
      getLabel={getLabel}
      getBadge={getBadge}
      getDescription={getDescription}
      getIcon={getAvatarOptionIcon}
      getChipColor={getAvatarOptionChipColor}
      getChipLabel={getChipLabel}
      getGroup={
        shouldGroupByOrg
          ? (site) => getOrgGroupHeading(site, noOrgGroupLabel)
          : undefined
      }
      isItemDisabled={isItemDisabled}
      getDisabledReason={getDisabledReason}
      isMulti={isMulti}
      maxSelected={maxSelected}
      isDisabled={!hasSites}
      message={message}
      messageTone={canShowHelp ? 'information' : 'error'}
      placeholder={formatMessage(mcpSiteSelectorMessages.sitePlaceholder)}
      renderLabel={() => (
        <FieldLabel>
          {formatMessage(mcpSiteSelectorMessages.siteLabel)}
        </FieldLabel>
      )}
      onChange={(urls) =>
        setSelectedWorkspace?.(serializeSelectedSiteUrls(urls))
      }
    />
  );
};

export default SiteSelector;
